/**
 * AuthCallback - Handles Google OAuth callback
 * Receives session_id from Google and exchanges it for user data
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

const AuthCallback = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      const sessionId = searchParams.get('session_id');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setStatus('error');
        setError(errorParam === 'access_denied' ? 'Η σύνδεση ακυρώθηκε' : errorParam);
        return;
      }

      if (!sessionId) {
        setStatus('error');
        setError('Δεν βρέθηκε session ID');
        return;
      }

      try {
        const BACKEND_URL = getBackendUrl();
        
        // Exchange session_id for user data
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/google/session`,
          {},
          {
            headers: {
              'X-Session-ID': sessionId
            },
            withCredentials: true
          }
        );

        if (response.data.status === 'success') {
          // Save token and user info
          storage.setItem('token', response.data.token);
          storage.setItem('user', JSON.stringify({
            user_id: response.data.user_id,
            username: response.data.username,
            email: response.data.email,
            name: response.data.name,
            role: response.data.role,
            subscription_status: response.data.subscription_status,
            auth_provider: 'google',
            picture: response.data.picture
          }));

          // Call parent success callback if provided
          if (onLoginSuccess) {
            onLoginSuccess({
              user_id: response.data.user_id,
              username: response.data.username,
              email: response.data.email,
              role: response.data.role,
              subscription_status: response.data.subscription_status
            });
          }

          setStatus('success');
          
          // Redirect to home after short delay
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          throw new Error(response.data.detail || 'Login failed');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setError(err.response?.data?.detail || err.message || 'Σφάλμα σύνδεσης');
      }
    };

    processCallback();
  }, [searchParams, navigate, onLoginSuccess]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md border-2 border-purple-200 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700">Σύνδεση με Google...</h2>
            <p className="text-gray-500 mt-2">Παρακαλώ περιμένετε</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-green-600">Επιτυχής σύνδεση!</h2>
            <p className="text-gray-500 mt-2">Μεταφορά στην αρχική...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-red-600">Σφάλμα σύνδεσης</h2>
            <p className="text-gray-500 mt-2">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Επιστροφή στη σύνδεση
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
